/**
 * useDocViewer 훅
 *
 * 책임:
 * - docparser 산출물(md 1건 + 이미지 N건)을 인증 fetch로 받아 뷰어에 넘긴다.
 * - md 본문의 상대 이미지 참조(![](images/x.jpg))를 objectURL로 치환한다.
 * - 언마운트/재조회 시 objectURL을 반드시 revoke (누수 방지).
 *
 * 왜 objectURL인가:
 *   이 앱은 토큰을 Authorization 헤더로 싣는데 <img src>는 헤더를 못 실는다.
 *   그래서 이미지도 Blob으로 받아 objectURL을 만들어 src에 꽂는다.
 *
 * 백엔드 doc-file 엔드포인트가 아직 없을 수 있다 → 실패해도 throw하지 않고
 * unavailable=true로 알려서, 뷰어가 "원본 PDF로 보기" 폴백을 보여주게 한다.
 *
 * DocViewer(organism)에서 사용.
 */

import { useEffect, useState } from 'react';
import * as endpoints from '../api/endpoints';

export interface UseDocViewerReturn {
  /** 이미지 참조가 objectURL로 치환된 마크다운 본문. 아직/실패면 null. */
  markdown: string | null;
  loading: boolean;
  /** 문서 파일을 가져오지 못함(엔드포인트 미배포·404 등) → PDF 폴백 안내용. */
  unavailable: boolean;
  /** 사용자에게 보여줄 실패 사유. unavailable일 때만 채워진다. */
  error: string | null;
}

/** Blob → objectURL. 호출자가 revoke 책임을 진다. */
function toObjectUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

/**
 * md 본문의 이미지 참조를 objectURL로 바꾼다.
 *
 * 스냅샷의 image_paths는 `<module>/<batchId>/images/x.jpg`(배치 기준 상대경로)인데,
 * md 안의 참조는 `images/x.jpg`처럼 배치 디렉토리 기준이다. 그래서 파일명(basename)으로
 * 맞춘다 — 한 배치의 images/ 안에서 이름은 유일하다(수집기가 중복 시 접미사를 붙임).
 *
 * 두 가지 표기를 모두 처리한다 — 변환기가 표를 원시 HTML로 내보내는 문서에서는
 * 이미지도 `<img src="images/x.jpg">` 형태로 섞여 나온다.
 */
function inlineImages(markdown: string, urlByBaseName: Map<string, string>): string {
  // 참조 경로 → objectURL. 외부 URL(http…)과 매칭 실패는 원문 유지.
  const resolve = (ref: string): string | null => {
    if (/^https?:\/\//i.test(ref)) return null;
    const base = ref.split('/').pop() ?? ref;
    return urlByBaseName.get(base) ?? null;
  };

  // 1) 마크다운 표기: ![alt](경로)
  const md = markdown.replace(/(!\[[^\]]*\]\()([^)\s]+)(\)|\s)/g, (whole, head, ref, tail) => {
    const url = resolve(ref);
    return url ? `${head}${url}${tail}` : whole;
  });

  // 2) 원시 HTML 표기: <img ... src="경로" ...>. 따옴표 종류("/')를 그대로 유지한다.
  return md.replace(
    /(<img\b[^>]*?\bsrc\s*=\s*)(["'])([^"']+)\2/gi,
    (whole, head, quote, ref) => {
      const url = resolve(ref);
      return url ? `${head}${quote}${url}${quote}` : whole;
    }
  );
}

/**
 * @param module    모듈명 (예: comp_earnings)
 * @param mdPath    스냅샷 row의 md_path. null이면 아무것도 하지 않음.
 * @param imagePaths 스냅샷 row의 image_paths
 * @param enabled   뷰어가 열려 있을 때만 true (닫혀 있으면 받지 않는다)
 */
export function useDocViewer(
  module: string,
  mdPath: string | null,
  imagePaths: string[],
  enabled: boolean
): UseDocViewerReturn {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // imagePaths는 매 렌더 새 배열일 수 있어 deps에 직접 넣으면 무한 루프가 난다 → 키로 고정.
  const imageKey = imagePaths.join('|');

  useEffect(() => {
    if (!enabled || !mdPath) {
      setMarkdown(null);
      setUnavailable(false);
      setError(null);
      return;
    }

    let alive = true;
    const created: string[] = []; // 이 실행에서 만든 objectURL — cleanup에서 전부 revoke
    const paths = imageKey ? imageKey.split('|') : [];

    setLoading(true);
    setUnavailable(false);
    setError(null);

    (async () => {
      try {
        // md 본문 먼저 — 이게 실패하면 문서를 못 보여준다.
        const { blob } = await endpoints.downloadDocFile(module, mdPath);
        const text = await blob.text();

        // 이미지들은 개별 실패를 허용한다 (일부 빠져도 본문은 읽을 수 있어야 함).
        const urlByBaseName = new Map<string, string>();
        await Promise.all(
          paths.map(async (p) => {
            try {
              const img = await endpoints.downloadDocFile(module, p);
              if (!alive) return;
              const url = toObjectUrl(img.blob);
              created.push(url);
              urlByBaseName.set(p.split('/').pop() ?? p, url);
            } catch {
              // 이미지 1건 실패는 무시 — md의 해당 참조는 원문 그대로 남는다.
            }
          })
        );

        if (!alive) return;
        setMarkdown(inlineImages(text, urlByBaseName));
      } catch (err) {
        if (!alive) return;
        // 문서 서빙 엔드포인트가 아직 없거나(404) 접근 실패 → PDF 폴백 안내
        setUnavailable(true);
        setError(err instanceof Error ? err.message : '문서 파일을 불러오지 못했습니다.');
        setMarkdown(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [module, mdPath, imageKey, enabled]);

  return { markdown, loading, unavailable, error };
}
